/**
 * MQTT Telemetry Service — Singleton
 *
 * Publishes device heartbeats and alerts to the CARO monitoring service
 * via MQTT over WebSocket. Also subscribes to command topics for remote
 * control (popups, TTS, ringtones, navigation).
 *
 * All topics use userId as the device identifier (stable across device
 * changes, unlike Android's randomized MAC addresses).
 *
 * Topics (publish):
 *   caro/device/{userId}/heartbeat    — periodic status (QoS 0)
 *   caro/device/{userId}/alert        — critical events (QoS 1)
 *   caro/device/{userId}/register     — device registration on connect (QoS 1, retained)
 *
 * Topics (subscribe):
 *   caro/device/{userId}/command      — receive remote commands (QoS 1)
 *   caro/device/{userId}/session      — session enforcement (single device)
 */
import mqtt from 'mqtt';

// ── Types ──────────────────────────────────────────────────────

export interface TelemetryConfig {
  brokerUrl: string;      // wss://mqtt.caro.care/mqtt
  username: string;
  password: string;
  userId: number;         // Numeric user ID — primary identifier for all topics
  macAddress: string;     // Device MAC or ANDROID_ID (informational only, not used in topics)
  sipExtension: string;   // SIP extension (e.g. "1001")
  appVersion: string;
  userName: string;       // Display name for registration
  locationId?: number;
  locationName?: string;
}

/**
 * Command types that can be received via MQTT.
 */
export type CommandType =
  | 'show_message'     // Show popup with text
  | 'speak_tts'        // Speak text aloud
  | 'play_ringtone'    // Play a sound/ringtone
  | 'navigate'         // Navigate to a URL or in-app route
  | 'bring_to_front'   // Bring app to foreground
  | 'refresh'          // Force refresh app state
  | 'reboot_webview'   // Reload the WebView
  | 'emergency'        // Drive the remote emergency screen (automation-owned)
  | 'display_settings' // Apply portal-pushed per-location display defaults
  | 'debug_overlay'    // Remotely show/hide the in-app diagnostics overlay (per device)
  // Bewonersmeldingen (spec 20260831-domotica-meldingen-bewonerstablet, T-2.03).
  //
  // ⚠ EIGEN TYPES, GEEN UITBREIDING VAN `show_message`. Dat commando heeft een vaste
  // betekenis op 800+ tablets met gemengde bundles; er velden in duwen breekt oudere
  // versies stil. Een bundle die deze twee niet kent, valt in de `default`-tak van
  // `useMqttCommands` en negeert ze — en dát is het gewenste degradatiegedrag: de melding
  // staat toch al in de database en wordt bij de volgende poll alsnog opgehaald.
  | 'resident_notification'       // Toon een bewonersmelding (persist-first, zie NotificationStore)
  | 'resident_notification_clear' // Trek een melding weer in
  // 20261016-taal-bewonersapp. Een oudere bundle valt in de `default`-tak en negeert ze.
  | 'profile_refresh'       // Profiel opnieuw ophalen (taal van bewoner of site gewijzigd)
  | 'announcements_changed' // Aankondigingen opnieuw ophalen
  | 'force_logout';    // Force logout (session enforcement)

export interface MqttCommand {
  id: string;               // Unique command ID
  type: CommandType;
  payload: {
    // show_message
    title?: string;
    message?: string;
    duration?: number;       // Auto-dismiss in ms (0 = manual dismiss)
    priority?: 'normal' | 'urgent';
    // speak_tts
    text?: string;
    lang?: string;           // BCP-47 tag (e.g. 'nl-NL')
    // play_ringtone
    sound?: 'ringtone' | 'alarm' | 'notification' | 'chime';
    repeat?: number;         // Number of repeats (0 = once)
    // navigate
    url?: string;            // In-app route (/settings) or full URL (https://...)
    // force_logout
    reason?: string;
    newDeviceMac?: string;
    // emergency — automation-driven tablet alarm state:
    //   state 'alarm_mode' + seconds → tablet counts down to 0 and freezes
    //   state 'reset'                → tablet returns to normal
    state?: 'alarm_mode' | 'reset';
    seconds?: number;
    // resident_notification — de volledige melding zit in dit veld, niet losse
    // sleutels op payload-niveau. Vorm: `ResidentNotificationPayload` uit
    // `@caro/shared/types`. `resident_notification_clear` draagt alleen
    // `notificationId` en/of `dedupeKey`.
    notification?: unknown;
    notificationId?: string;
    dedupeKey?: string;
    // display_settings — per-location display defaults pushed from the portal:
    theme?: 'light' | 'dark' | 'high-contrast';
    clockStyle?: 'digital' | 'analog';
    fontSize?: 'normal' | 'large' | 'xlarge' | 'huge' | 'giant';
    screensaverEnabled?: boolean;
    // Generic
    [key: string]: unknown;
  };
  timestamp: string;
  source?: string;           // Who sent it (e.g. 'portal', 'automation', 'session')
}

export interface HeartbeatPayload {
  status: 'ok' | 'degraded' | 'error';
  timestamp: string;
  agent_version: string;
  device: {
    mac_address: string;
    user_id: number;
    user_name: string;
    sip_extension: string;
    location_id?: number;
    location_name?: string;
  };
  services: {
    mqtt: { status: string };
    network: { status: string };
    sip: { status: string; extension?: string };
  };
  system: {
    battery_level: number;
    battery_charging: boolean;
    connection_type: string;
  };
  permissions: {
    all_granted: boolean;
    missing: string[];
  };
}

export type MqttConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

// ── Singleton state ──────────────────────────────────────────

let client: mqtt.MqttClient | null = null;
let connectionState: MqttConnectionState = 'disconnected';
let heartbeatInterval: ReturnType<typeof setInterval> | null = null;
let config: TelemetryConfig | null = null;

// External store for React consumers (useSyncExternalStore pattern)
const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach(l => l());
}

// ── Command handling (module-level, not tied to React) ────────
const commandListeners = new Set<(cmd: MqttCommand) => void>();

export function onCommand(callback: (cmd: MqttCommand) => void): () => void {
  commandListeners.add(callback);
  return () => { commandListeners.delete(callback); };
}

export function dispatchCommand(cmd: MqttCommand) {
  console.log('[MQTT] Command received:', cmd.type, cmd.id);
  commandListeners.forEach(cb => {
    try {
      cb(cmd);
    } catch (e) {
      console.error('[MQTT] Command handler error:', e);
    }
  });
}

// ── Session enforcement ──────────────────────────────────────
const sessionListeners = new Set<(reason: string) => void>();

export function onSessionConflict(callback: (reason: string) => void): () => void {
  sessionListeners.add(callback);
  return () => { sessionListeners.delete(callback); };
}

function dispatchSessionConflict(reason: string) {
  console.log('[MQTT] Session conflict — forcing logout:', reason);
  sessionListeners.forEach(cb => {
    try {
      cb(reason);
    } catch (e) {
      console.error('[MQTT] Session conflict handler error:', e);
    }
  });
}

// ── Data collector ────────────────────────────────────────────
let getStatusData: (() => Omit<HeartbeatPayload, 'timestamp' | 'agent_version' | 'device'>) | null = null;

export function setStatusDataCollector(
  collector: () => Omit<HeartbeatPayload, 'timestamp' | 'agent_version' | 'device'>
) {
  getStatusData = collector;
}

// ── Helper: topic prefix ─────────────────────────────────────
function topicPrefix(): string {
  return `caro/device/${config!.userId}`;
}

// ── Core methods ─────────────────────────────────────────────

export function connect(cfg: TelemetryConfig) {
  if (client) return;
  config = cfg;
  connectionState = 'connecting';
  notifyListeners();

  console.log('[MQTT] Connecting to', cfg.brokerUrl, 'as user', cfg.userId);

  try {
    client = mqtt.connect(cfg.brokerUrl, {
      username: cfg.username,
      password: cfg.password,
      clientId: `caro-resident-${cfg.userId}-${Date.now()}`,
      clean: true,
      keepalive: 60,
      reconnectPeriod: 5000,
      connectTimeout: 10000,
    });
  } catch (err) {
    console.error('[MQTT] Failed to connect (WebSocket error):', err);
    client = null;
    connectionState = 'error';
    notifyListeners();
    return;
  }

  client.on('connect', () => {
    connectionState = 'connected';
    console.log('[MQTT] Connected');
    notifyListeners();

    const prefix = topicPrefix();

    // 1. Subscribe to command topic (userId-based)
    const commandTopic = `${prefix}/command`;
    client!.subscribe(commandTopic, { qos: 1 }, (err) => {
      if (err) {
        console.error('[MQTT] Command subscribe failed:', err.message);
      } else {
        console.log('[MQTT] Subscribed to commands:', commandTopic);
      }
    });

    // 2. Subscribe to session topic for single-device enforcement
    const sessionTopic = `${prefix}/session`;
    client!.subscribe(sessionTopic, { qos: 1 }, (err) => {
      if (err) {
        console.error('[MQTT] Session subscribe failed:', err.message);
      } else {
        console.log('[MQTT] Subscribed to session topic:', sessionTopic);
        publishSessionClaim();
      }
    });

    // 3. Publish device registration
    publishRegistration();

    // 4. Start heartbeat
    startHeartbeat();
  });

  client.on('error', (err) => {
    console.error('[MQTT] Error:', err.message);
    connectionState = 'error';
    notifyListeners();
  });

  client.on('close', () => {
    console.log('[MQTT] Connection closed');
    connectionState = 'disconnected';
    notifyListeners();
  });

  client.on('reconnect', () => {
    console.log('[MQTT] Reconnecting...');
    connectionState = 'connecting';
    notifyListeners();
  });

  // Handle incoming messages (commands + session)
  client.on('message', (topic: string, message: Buffer) => {
    try {
      console.log('[MQTT] Message on topic:', topic);
      const payload = JSON.parse(message.toString());
      const prefix = topicPrefix();

      // Session enforcement messages
      if (topic === `${prefix}/session`) {
        handleSessionMessage(payload);
        return;
      }

      // Command messages
      if (topic === `${prefix}/command`) {
        const cmd = payload as MqttCommand;
        if (!cmd.type || !cmd.id) {
          console.warn('[MQTT] Invalid command (missing type or id):', message.toString());
          return;
        }
        if (cmd.type === 'force_logout') {
          dispatchSessionConflict(cmd.payload?.reason || 'Remote logout requested');
          return;
        }
        dispatchCommand(cmd);
        return;
      }
    } catch (e) {
      console.error('[MQTT] Failed to parse message:', e);
    }
  });
}

/**
 * Publish session claim — tells other devices with same user to logout.
 */
function publishSessionClaim() {
  if (!client || !config) return;

  const topic = `${topicPrefix()}/session`;
  const payload = {
    action: 'session_claim',
    macAddress: config.macAddress,
    userId: config.userId,
    sipExtension: config.sipExtension,
    userName: config.userName,
    timestamp: new Date().toISOString(),
  };

  client.publish(topic, JSON.stringify(payload), { qos: 1, retain: false }, (err) => {
    if (err) {
      console.error('[MQTT] Failed to publish session claim:', err.message);
    } else {
      console.log('[MQTT] Session claim published for user', config!.userId);
    }
  });
}

function handleSessionMessage(payload: any) {
  if (!config) return;

  if (payload.action === 'session_claim') {
    if (payload.macAddress === config.macAddress) {
      console.log('[MQTT] Ignoring own session claim');
      return;
    }
    console.warn('[MQTT] Another device claimed session!', {
      otherMac: payload.macAddress,
      ourMac: config.macAddress,
      user: payload.userId,
    });
    dispatchSessionConflict(
      `Another device logged in as this user (${payload.macAddress})`
    );
  }
}

function publishRegistration() {
  if (!client || !config) return;

  const topic = `${topicPrefix()}/register`;
  const payload = {
    type: 'resident_app',
    userId: config.userId,
    userName: config.userName,
    macAddress: config.macAddress,
    sipExtension: config.sipExtension,
    appVersion: config.appVersion,
    locationId: config.locationId,
    locationName: config.locationName,
    timestamp: new Date().toISOString(),
  };

  client.publish(topic, JSON.stringify(payload), { qos: 1, retain: true }, (err) => {
    if (err) {
      console.error('[MQTT] Failed to publish registration:', err.message);
    } else {
      console.log('[MQTT] Device registered: user', config!.userId, 'SIP:', config!.sipExtension);
    }
  });
}

/**
 * Update the SIP extension after SIP registers.
 */
export function updateSipExtension(extension: string) {
  if (!config || config.sipExtension === extension) return;
  config.sipExtension = extension;
  if (client && connectionState === 'connected') {
    publishRegistration();
  }
}

export function disconnect() {
  stopHeartbeat();
  if (client) {
    client.end(true);
    client = null;
  }
  connectionState = 'disconnected';
  config = null;
  getStatusData = null;
  notifyListeners();
}

function startHeartbeat() {
  stopHeartbeat();
  sendHeartbeat();
  heartbeatInterval = setInterval(sendHeartbeat, 30_000);
}

function stopHeartbeat() {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
}

function sendHeartbeat() {
  if (!client || connectionState !== 'connected' || !config) return;

  const statusData = getStatusData?.() ?? {
    status: 'ok' as const,
    services: {
      mqtt: { status: 'running' },
      network: { status: 'unknown' },
      sip: { status: 'unknown' },
    },
    system: {
      battery_level: 100,
      battery_charging: false,
      connection_type: 'unknown',
    },
    permissions: { all_granted: true, missing: [] },
  };

  const liveSipExtension = statusData.services?.sip?.extension || config.sipExtension;

  const payload: HeartbeatPayload = {
    ...statusData,
    timestamp: new Date().toISOString(),
    agent_version: config.appVersion,
    device: {
      mac_address: config.macAddress,
      user_id: config.userId,
      user_name: config.userName,
      sip_extension: liveSipExtension,
      location_id: config.locationId,
      location_name: config.locationName,
    },
  };

  client.publish(`${topicPrefix()}/heartbeat`, JSON.stringify(payload), { qos: 0 });
}

/**
 * Send an alert for critical state changes (QoS 1 for reliability).
 */
export function sendAlert(alertPayload: {
  type: string;
  severity: 'warning' | 'error' | 'critical';
  message: string;
  service?: string;
}) {
  if (!client || connectionState !== 'connected' || !config) return;

  const payload = {
    ...alertPayload,
    timestamp: new Date().toISOString(),
    agent_version: config.appVersion,
    device: {
      mac_address: config.macAddress,
      sip_extension: config.sipExtension,
      user_id: config.userId,
    },
  };
  client.publish(`${topicPrefix()}/alert`, JSON.stringify(payload), { qos: 1 });
}

// ── External store exports (for useSyncExternalStore) ─────────

export function getConnectionState(): MqttConnectionState {
  return connectionState;
}

export function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => { listeners.delete(callback); };
}
